import { useCallback } from "react";

import { createApi } from "@reduxjs/toolkit/query/react";
import {
    ClpPositionInfo,
    ClpPositionInfoRaw,
    ParsedOrcaWhirlpool,
    TokenListMetadata,
    TokenListMetadataRaw,
    TokenListTag
} from "@bridgesplit/abf-sdk";
import { DEBUG, filterTruthy, removeDuplicates } from "@bridgesplit/utils";
import { useDispatch } from "react-redux";

import { TokenRestrictionResponse, TokenRestrictionsRequest } from "../types";
import { abfSerializeQueryArgs, marketsPublicBaseQuery } from "./util";
import {
    GET_TOKEN_LIST_ROUTE,
    GET_TOKEN_RESTRICTIONS_ROUTE,
    GET_WHIRLPOOL_POSITIONS_ROUTE,
    GET_WHIRLPOOL_ROUTE
} from "../constants";
import { parsedWhirlpoolPositions } from "./type-util/whirlpool";

function logTokenListWarning(message: string, detail: unknown): void {
    if (!DEBUG) return;
    // eslint-disable-next-line no-console
    console.warn(message, detail);
}

const VALID_TOKEN_LIST_TAGS = new Set<string>(Object.values(TokenListTag));

type ParsedTokenListTags = { tags: TokenListTag[]; unknownTags: string[] };

function parseTokenListTags(rawTags: string | undefined): ParsedTokenListTags {
    const trimmed = rawTags?.trim();
    if (!trimmed) return { tags: [], unknownTags: [] };

    let tagNames: string[];
    if (trimmed.startsWith("[")) {
        try {
            const parsed: unknown = JSON.parse(trimmed.replace(/'/g, '"'));
            if (!Array.isArray(parsed)) return { tags: [], unknownTags: [] };
            tagNames = parsed.map(String);
        } catch (e) {
            logTokenListWarning("Invalid tag format", { rawTags: trimmed, error: e });
            return { tags: [], unknownTags: [] };
        }
    } else {
        tagNames = trimmed.split(",");
    }

    const tags: TokenListTag[] = [];
    const unknownTags: string[] = [];
    for (const rawTag of tagNames) {
        const tag = rawTag.trim();
        if (!tag) continue;
        if (VALID_TOKEN_LIST_TAGS.has(tag)) tags.push(tag as TokenListTag);
        else unknownTags.push(tag);
    }

    return { tags, unknownTags };
}

function warnUnknownTokenListTags(unknownTagsByMint: Record<string, string[]>): void {
    const mints = Object.keys(unknownTagsByMint);
    if (!mints.length) return;
    logTokenListWarning(`Unmodeled token list tags on ${mints.length} token(s)`, unknownTagsByMint);
}

const CLMM_TAG = "CLMM";
const TOKEN_LIST_TAG = "TOKEN_LIST";
const TOKEN_RESTRICTIONS_TAG = "TOKEN_RESTRICTIONS";
// split token list to use unauthenticated base query
export const tokenListApi = createApi({
    reducerPath: "tokenListApi",
    baseQuery: marketsPublicBaseQuery,
    serializeQueryArgs: abfSerializeQueryArgs,
    tagTypes: [CLMM_TAG, TOKEN_LIST_TAG, TOKEN_RESTRICTIONS_TAG],
    endpoints: (builder) => ({
        tokenList: builder.query<TokenListMetadata[], void>({
            query() {
                return {
                    url: GET_TOKEN_LIST_ROUTE,
                    method: "GET"
                };
            },
            transformResponse: (raw: Record<string, TokenListMetadataRaw>): TokenListMetadata[] => {
                const unknownTagsByMint: Record<string, string[]> = {};

                const tokens = Object.values(raw).map((token) => {
                    const { tags: parsedTags, unknownTags } = parseTokenListTags(token.tags);
                    if (unknownTags.length) unknownTagsByMint[token.mint] = unknownTags;

                    let parsedMetadata = typeof token.metadata === "string" ? undefined : token.metadata;

                    if (typeof token.metadata === "string") {
                        try {
                            parsedMetadata = JSON.parse(token.metadata.replace(/'/g, '"'));
                        } catch (e) {
                            logTokenListWarning("Invalid metadata format", { rawMetadata: token.metadata, error: e });
                            parsedMetadata = undefined;
                        }
                    }

                    return {
                        ...token,
                        metadata: parsedMetadata,
                        tags: parsedTags
                    };
                });

                warnUnknownTokenListTags(unknownTagsByMint);

                return tokens;
            },
            providesTags: [TOKEN_LIST_TAG]
        }),
        clmmPositions: builder.query<ClpPositionInfo, string[]>({
            query(mints) {
                return {
                    url: GET_WHIRLPOOL_POSITIONS_ROUTE,
                    method: "POST",
                    body: mints
                };
            },
            transformResponse: (raw: ClpPositionInfoRaw): ClpPositionInfo => {
                return parsedWhirlpoolPositions(raw);
            },
            providesTags: [CLMM_TAG]
        }),
        whirlpools: builder.query<Record<string, ParsedOrcaWhirlpool>, string[]>({
            query(mints) {
                return {
                    url: GET_WHIRLPOOL_ROUTE,
                    method: "POST",
                    body: mints
                };
            }
        }),
        tokenRestrictions: builder.query<TokenRestrictionResponse[], TokenRestrictionsRequest>({
            query(args) {
                return {
                    url: GET_TOKEN_RESTRICTIONS_ROUTE,
                    method: "POST",
                    body: {
                        mints: args.mints,
                        country_code: args.countryCode
                    }
                };
            },
            providesTags: [TOKEN_RESTRICTIONS_TAG]
        })
    })
});

export const { useTokenListQuery, useClmmPositionsQuery, useWhirlpoolsQuery, useTokenRestrictionsQuery } = tokenListApi;

export function useWhirlpoolByAddresses(addresses: string[]) {
    const uniqueAddresses = removeDuplicates(addresses?.filter(filterTruthy) ?? []);
    const { data: addressToWhirlpool, isLoading, isFetching } = useWhirlpoolsQuery(uniqueAddresses);

    const getWhirlpool = useCallback(
        (address: string | undefined) => {
            return address ? addressToWhirlpool?.[address] : undefined;
        },
        [addressToWhirlpool]
    );

    return {
        getWhirlpool,
        isLoading,
        isFetching
    };
}

export const useTokenListApi = () => {
    const api = tokenListApi.endpoints;
    const dispatch = useDispatch();
    const [fetchTokenList] = api.tokenList.useLazyQuery();
    const [fetchClmmPositions] = api.clmmPositions.useLazyQuery();
    const [fetchTokenRestrictions] = api.tokenRestrictions.useLazyQuery();

    return {
        fetchTokenList,
        fetchClmmPositions,
        fetchTokenRestrictions,
        resetTokenList: () => dispatch(tokenListApi.util.invalidateTags([TOKEN_LIST_TAG])),
        resetClmmPositions: () => dispatch(tokenListApi.util.invalidateTags([CLMM_TAG])),
        resetTokenRestrictions: () => dispatch(tokenListApi.util.invalidateTags([TOKEN_RESTRICTIONS_TAG]))
    };
};
